// ─────────────────────────────────────────────────────────────────────────────
// BizPlus parity — Followup / Appointment panel  (Phase 0.1)
//
// One panel serves both entities. Drop it on any record detail page:
//
//   <FollowupPanel    parentType="lead" parentId={lead.id} />
//   <AppointmentPanel parentType="deal" parentId={deal.id} />
//
// The panel is the whole feature surface for a record: the log (newest first),
// an inline "add" form, and a one-click way to settle an open row. Overdue rows
// are called out, because an overdue followup is the thing the dashboard
// widgets and the reports are all counting.
// ─────────────────────────────────────────────────────────────────────────────

import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { CalendarClock, Plus, Check, AlertTriangle } from 'lucide-react';
import { Spinner } from '@/shared/components/ui/Spinner';
import { Badge } from '@/shared/components/ui/Badge';
import { EmptyState } from '@/shared/components/ui/EmptyState';
import { Button } from '@/shared/components/ui/Button';
import { Input } from '@/shared/components/ui/Input';
import { Select } from '@/shared/components/ui/Select';
import { cn, timeAgo } from '@/shared/utils/utils';
import { useHasPermission } from '@/shared/permissions/useHasPermission';
import { CloseFollowupDialog, outcomeLabel, type CloseFollowupTarget } from './CloseFollowupDialog';
import {
  useTimelineList,
  useCreateTimelineRow,
  useUpdateTimelineRow,
  dateFieldFor,
  dateOf,
  CONTACT_METHOD_OPTIONS,
  FOLLOWUP_CATEGORY_OPTIONS,
  type TimelineKind,
  type ParentType,
  type TimelineRow,
  type Followup,
  type Appointment,
} from '../hooks/useFollowups';

const FOLLOWUP_STATUS_OPTIONS = [
  { value: 'open', label: 'Open' },
  { value: 'done', label: 'Done' },
  { value: 'postponed', label: 'Postponed' },
  { value: 'interested', label: 'Interested' },
  { value: 'not_interested', label: 'Not interested' },
  { value: 'closed', label: 'Closed' },
];

const APPOINTMENT_STATUS_OPTIONS = [
  { value: 'open', label: 'Open' },
  { value: 'pending', label: 'Pending' },
  { value: 'appointment', label: 'Appointment set' },
  { value: 'interested', label: 'Interested' },
  { value: 'not_interested', label: 'Not interested' },
];

/** Statuses that leave a row outstanding — must mirror the server's sets. */
const OUTSTANDING: Record<TimelineKind, string[]> = {
  followups: ['open'],
  appointments: ['open', 'pending'],
};

const STATUS_TONE: Record<string, string> = {
  open: 'bg-info/10 text-info-text',
  pending: 'bg-warning/10 text-warning-text',
  done: 'bg-success/10 text-success-text',
  appointment: 'bg-primary-container text-on-primary-container',
  interested: 'bg-success/10 text-success-text',
  not_interested: 'bg-danger/10 text-danger-text',
  postponed: 'bg-warning/10 text-warning-text',
  closed: 'bg-surface-variant text-on-surface-variant',
};

const startOfToday = () => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
};

/** Outstanding AND dated before today — the definition the overdue feed uses. */
function isOverdue(kind: TimelineKind, row: TimelineRow): boolean {
  if (!OUTSTANDING[kind].includes(row.status)) return false;
  const when = new Date(dateOf(kind, row));
  return !Number.isNaN(when.getTime()) && when < startOfToday();
}

const labelFor = (kind: TimelineKind) =>
  kind === 'followups'
    ? { one: 'followup', many: 'Followups', add: 'Log followup' }
    : { one: 'appointment', many: 'Appointments', add: 'Add appointment' };

export interface TimelinePanelProps {
  kind: TimelineKind;
  parentType: ParentType;
  parentId: string | null | undefined;
  className?: string;
  /** List rows logged under any parent type for this id (a pipeline record). New rows still use parentType. */
  anyParentType?: boolean;
}

export function TimelinePanel({ kind, parentType, parentId, className, anyParentType = false }: TimelinePanelProps) {
  const location = useLocation();
  const sectionRef = useRef<HTMLElement>(null);
  const label = labelFor(kind);
  const dateField = dateFieldFor(kind);
  const statusOptions = kind === 'followups' ? FOLLOWUP_STATUS_OPTIONS : APPOINTMENT_STATUS_OPTIONS;

  const { data: rows = [], isLoading, isError } = useTimelineList(kind, parentType, parentId, { anyParentType });
  useEffect(() => {
    if (location.hash === `#${kind}` && !isLoading) {
      sectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      sectionRef.current?.focus({ preventScroll: true });
    }
  }, [location.key, location.hash, kind, parentId, isLoading]);
  const create = useCreateTimelineRow(kind);
  const update = useUpdateTimelineRow(kind);

  // Hide the controls a caller cannot use, so the UI never offers an action the
  // server will refuse. The server enforces the same key regardless.
  const canWrite = useHasPermission(`${kind}.write`);

  const [open, setOpen] = useState(false);
  const [note, setNote] = useState('');
  const [when, setWhen] = useState('');
  const [details, setDetails] = useState('');
  const [contactMethod, setContactMethod] = useState('');
  const [followupCategory, setFollowupCategory] = useState('');
  const [customCategory, setCustomCategory] = useState('');
  const [customerResponse, setCustomerResponse] = useState('');
  const [requirement, setRequirement] = useState('');
  const [nextAction, setNextAction] = useState('');
  const [error, setError] = useState<string | null>(null);
  // Closing a follow-up asks for its outcome and the next one (client, 2026-10-28).
  const [closing, setClosing] = useState<CloseFollowupTarget | null>(null);

  const reset = () => {
    setNote('');
    setWhen('');
    setDetails('');
    setContactMethod('');
    setFollowupCategory('');
    setCustomCategory('');
    setCustomerResponse('');
    setRequirement('');
    setNextAction('');
    setError(null);
    setOpen(false);
  };

  const submit = () => {
    if (!parentId) return;
    if (!when) {
      setError('Pick a date.');
      return;
    }
    if (kind === 'followups' && !note.trim()) {
      setError('Add a short note so the next person knows what happened.');
      return;
    }

    const payload: Record<string, unknown> = {
      parentType,
      parentId,
      [dateField]: new Date(when).toISOString(),
      details: details.trim(),
      status: 'open',
    };
    if (kind === 'followups') {
      payload.followupNote = note.trim();
      payload.contactMethod = contactMethod;
      payload.followupCategory = followupCategory || undefined;
      payload.customCategory = followupCategory === 'custom' ? (customCategory.trim() || undefined) : undefined;
      payload.customerResponse = customerResponse.trim();
      payload.requirement = requirement.trim();
      payload.nextAction = nextAction.trim();
    }

    create.mutate(payload, {
      onSuccess: reset,
      onError: () => setError(`Could not save the ${label.one}. Try again.`),
    });
  };

  const settle = (row: TimelineRow) => {
    if (kind === 'followups') {
      const f = row as Followup;
      setClosing({ id: f._id, parentType: f.parentType || parentType, parentId: f.parentId || (parentId as string), note: f.followupNote });
      return;
    }
    update.mutate({
      id: row._id,
      patch: {
        status: 'appointment',
        parentType,
        parentId: parentId as string,
      },
    });
  };

  const overdueCount = rows.filter((r) => isOverdue(kind, r)).length;

  return (
    <section ref={sectionRef} id={kind} tabIndex={-1} className={cn('scroll-mt-24 rounded-xl border border-outline bg-surface focus:ring-2 focus:ring-indigo-400', className)}>
      <header className="flex items-center justify-between gap-3 border-b border-outline px-4 py-3">
        <div className="flex items-center gap-2">
          <CalendarClock className="h-4 w-4 text-on-surface-variant" aria-hidden />
          <h3 className="text-sm font-semibold text-on-surface">{label.many}</h3>
          {rows.length > 0 && (
            <span className="text-xs text-on-surface-variant">({rows.length})</span>
          )}
          {overdueCount > 0 && (
            <Badge tone="danger" className="gap-1">
              <AlertTriangle className="h-3 w-3" aria-hidden />
              {overdueCount} overdue
            </Badge>
          )}
        </div>
        {canWrite && (
          <Button size="sm" variant="ghost" onClick={() => setOpen((v) => !v)} disabled={!parentId}>
            <Plus className="mr-1 h-4 w-4" aria-hidden />
            {label.add}
          </Button>
        )}
      </header>

      {open && (
        <div className="space-y-3 border-b border-outline bg-surface-variant/30 px-4 py-3">
          {kind === 'followups' && (
            <>
              <Select
                label="Category"
                name="followup-category"
                options={FOLLOWUP_CATEGORY_OPTIONS.map((o) => ({ label: o.label, value: o.value }))}
                placeholder="Select category"
                value={followupCategory}
                onChange={(e) => setFollowupCategory(e.target.value)}
              />
              {followupCategory === 'custom' && (
                <Input
                  label="Custom Category"
                  placeholder="Type custom category name (free to type)"
                  value={customCategory}
                  onChange={(e) => setCustomCategory(e.target.value)}
                  maxLength={100}
                />
              )}
              <Input
                label="Note"
                placeholder="What happened, and what's next?"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                maxLength={300}
              />
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <Select
                  label="Contact method"
                  name="followup-contact-method"
                  options={CONTACT_METHOD_OPTIONS.filter((o) => o.value).map((o) => ({ label: o.label, value: o.value }))}
                  placeholder="Not recorded"
                  value={contactMethod}
                  onChange={(e) => setContactMethod(e.target.value)}
                />
                <Input
                  label="Customer response (optional)"
                  placeholder="What did they say?"
                  value={customerResponse}
                  onChange={(e) => setCustomerResponse(e.target.value)}
                  maxLength={2000}
                />
                <Input
                  label="Requirement (optional)"
                  placeholder="What do they need?"
                  value={requirement}
                  onChange={(e) => setRequirement(e.target.value)}
                  maxLength={2000}
                />
                <Input
                  label="Next action (optional)"
                  placeholder="What happens next?"
                  value={nextAction}
                  onChange={(e) => setNextAction(e.target.value)}
                  maxLength={2000}
                />
              </div>
            </>
          )}
          <Input
            label={kind === 'followups' ? 'Next followup on' : 'Appointment on'}
            type="datetime-local"
            value={when}
            onChange={(e) => setWhen(e.target.value)}
          />
          <Input
            label="Details (optional)"
            placeholder="Anything worth keeping on the record"
            value={details}
            onChange={(e) => setDetails(e.target.value)}
          />
          {error && <p className="text-xs text-danger-text">{error}</p>}
          <div className="flex justify-end gap-2">
            <Button size="sm" variant="ghost" onClick={reset}>Cancel</Button>
            <Button size="sm" onClick={submit} loading={create.isPending}>Save</Button>
          </div>
        </div>
      )}

      <div className="px-4 py-3">
        {isLoading ? (
          <div className="flex items-center justify-center py-8"><Spinner /></div>
        ) : isError ? (
          <EmptyState
            title={`Could not load ${label.many.toLowerCase()}`}
            description="Check your connection or try refreshing."
          />
        ) : rows.length === 0 ? (
          <EmptyState
            title={`No ${label.many.toLowerCase()} yet`}
            description={
              kind === 'followups'
                ? 'Log the first followup to start the contact history on this record.'
                : 'Schedule the first appointment against this record.'
            }
          />
        ) : (
          <ol className="space-y-3">
            {rows.map((row) => {
              const overdue = isOverdue(kind, row);
              const outstanding = OUTSTANDING[kind].includes(row.status);
              const headline =
                kind === 'followups'
                  ? (row as Followup).followupNote
                  : (row as Appointment).reason || (row as Appointment).details || 'Appointment';

              return (
                <li key={row._id} className="flex gap-3">
                  <span
                    className={cn(
                      'mt-1.5 h-2 w-2 shrink-0 rounded-full',
                      overdue ? 'bg-danger' : outstanding ? 'bg-info' : 'bg-outline',
                    )}
                    aria-hidden
                  />
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="text-sm font-medium text-on-surface">{headline}</p>
                      <span
                        className={cn(
                          'rounded-full px-2 py-0.5 text-[11px] font-medium',
                          STATUS_TONE[row.status] ?? STATUS_TONE.closed,
                        )}
                      >
                        {statusOptions.find((o) => o.value === row.status)?.label ?? row.status}
                      </span>
                      {kind === 'followups' && (row as Followup).followupCategory && (
                        <span className="rounded-full bg-slate-100 border border-slate-200 px-2 py-0.5 text-[11px] font-medium text-slate-700">
                          {(row as Followup).followupCategory === 'custom' && (row as Followup).customCategory
                            ? (row as Followup).customCategory
                            : (FOLLOWUP_CATEGORY_OPTIONS.find((c) => c.value === (row as Followup).followupCategory)?.label || (row as Followup).followupCategory)}
                        </span>
                      )}
                      {kind === 'followups' && (row as Followup).outcome && (
                        <span className="rounded-full border border-primary/25 bg-primary/10 px-2 py-0.5 text-[11px] font-semibold text-primary">
                          {outcomeLabel((row as Followup).outcome)}
                        </span>
                      )}
                      {overdue && (
                        <span className="text-[11px] font-medium text-danger-text">Overdue</span>
                      )}
                    </div>
                    {row.details && (
                      <p className="mt-0.5 text-xs text-on-surface-variant">{row.details}</p>
                    )}
                    {kind === 'followups' && (row as Followup).customerResponse && (
                      <p className="mt-0.5 text-xs italic text-on-surface-variant">
                        "{(row as Followup).customerResponse}"
                      </p>
                    )}
                    {kind === 'followups' && (row as Followup).noNextReason && (
                      <p className="mt-0.5 text-xs text-on-surface-variant"><span className="font-medium">No next follow-up:</span> {(row as Followup).noNextReason}</p>
                    )}
                    {kind === 'followups' && (row as Followup).requirement && (
                      <p className="mt-0.5 text-xs text-on-surface-variant"><span className="font-medium">Requirement:</span> {(row as Followup).requirement}</p>
                    )}
                    {kind === 'followups' && (row as Followup).nextAction && (
                      <p className="mt-0.5 text-xs text-on-surface-variant"><span className="font-medium">Next:</span> {(row as Followup).nextAction}</p>
                    )}
                    <p className="mt-0.5 text-[11px] text-on-surface-variant">
                      {kind === 'followups' && (row as Followup).contactMethod && (
                        <>
                          {CONTACT_METHOD_OPTIONS.find((o) => o.value === (row as Followup).contactMethod)?.label ?? (row as Followup).contactMethod}
                          {' · '}
                        </>
                      )}
                      {new Date(dateOf(kind, row)).toLocaleString()} · logged {timeAgo(row.created_at)}
                    </p>
                  </div>
                  {outstanding && canWrite && (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => settle(row)}
                      disabled={update.isPending}
                      aria-label={`Mark this ${label.one} done`}
                    >
                      <Check className="h-4 w-4" aria-hidden />
                    </Button>
                  )}
                </li>
              );
            })}
          </ol>
        )}
      </div>
    <CloseFollowupDialog target={closing} onClose={() => setClosing(null)} />
    </section>
  );
}

/** Followups for one record. */
export function FollowupPanel(props: Omit<TimelinePanelProps, 'kind'>) {
  return <TimelinePanel kind="followups" {...props} />;
}

/** Appointments for one record. */
export function AppointmentPanel(props: Omit<TimelinePanelProps, 'kind'>) {
  return <TimelinePanel kind="appointments" {...props} />;
}
